import React from 'react';
import Box from '@mui/material/Box';

import { color, r, tabularNums } from '../../theme/tokens';

// RelevanceRing — how well a supplement fits YOU, 0–5. A green conic arc with
// the score in the middle.
//
// This encodes RELEVANCE, never study strength. The two must not be confused:
// strength is the graphite segment bar (`color.strengthFill`), and they answer
// different questions. Geometry is the handoff's `RelevanceRing` component
// verbatim — 44px outer, inner disc at 73% of it, arc at `score / max * 100`
// percent.
//
// Note it will sit at 4.0 or 5.0 for nearly every supplement today: the backend
// field behind `evidenceScore` is integer-cast and taken as a max across a
// supplement's studies, so in practice it only ever lands on 3, 4 or 5. That is
// a known, ruled consequence (STACK-480) and not a defect in this component —
// the ring needs no change when the backend starts emitting decimals.

export const RING_MAX = 5;

// Outer diameters the handoff draws, in px. Converted to rem at render.
const INNER_RATIO = 0.73;

/** Font size (px) for the centred score, per the handoff's size ladder. */
export const ringFontPx = (size) => (size <= 36 ? 10 : size <= 48 ? 12 : 14);

/**
 * Coerce whatever the API gave us into a finite number, or NaN.
 * `null`, `undefined`, `''` and non-numeric strings all mean "no score".
 */
const toScore = (value) => {
  if (value === null || value === undefined || value === '') return Number.NaN;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : Number.NaN;
};

/** Filled percentage of the arc, clamped to 0–100. No score → an empty track. */
export const ringPercent = (score, max = RING_MAX) => {
  const numeric = toScore(score);
  if (Number.isNaN(numeric) || !max) return 0;
  return Math.max(0, Math.min(100, (numeric / max) * 100));
};

/**
 * The centred label. An em dash when there is no score — an absent score is a
 * real state (a supplement added by hand has never been scored), and `0.0`
 * would assert "we rated this zero", which is a different claim.
 *
 * Out-of-range scores are clamped to the scale rather than printed raw, so the
 * number can never contradict the arc beside it.
 */
export const ringLabel = (score, max = RING_MAX) => {
  const numeric = toScore(score);
  if (Number.isNaN(numeric)) return '—';
  return Math.max(0, Math.min(max, numeric)).toFixed(1);
};

const RelevanceRing = ({
  score,
  max = RING_MAX,
  size = 44,
  label = 'Relevance to your goal and profile',
  sx,
  ...rest
}) => {
  const pct = ringPercent(score, max);
  const text = ringLabel(score, max);
  const inner = Math.round(size * INNER_RATIO);

  return (
    <Box
      role="img"
      aria-label={text === '—' ? `${label}: not scored` : `${label}: ${text} out of ${max}`}
      sx={{
        width: r(size),
        height: r(size),
        borderRadius: '50%',
        background: `conic-gradient(${color.accent} ${pct}%, ${color.ringTrack} 0)`,
        display: 'grid',
        placeItems: 'center',
        flex: 'none',
        ...sx,
      }}
      {...rest}
    >
      <Box
        aria-hidden="true"
        sx={{
          width: r(inner),
          height: r(inner),
          borderRadius: '50%',
          backgroundColor: color.paper,
          display: 'grid',
          placeItems: 'center',
          fontWeight: 700,
          fontSize: r(ringFontPx(size)),
          color: color.evergreen,
          ...tabularNums,
        }}
      >
        {text}
      </Box>
    </Box>
  );
};

export default RelevanceRing;
